/**
 * Page-level data + real-time wiring for the Lumyn Live host dashboard.
 *
 * Owns: session list (load / search / sort), the active session, websocket
 * push + polling fallback refresh, deep-link from the "Run live" CTA, and
 * in-place rename patching.
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useApi } from './useApi';
import { useLiveSocket } from './useLiveSocket';

const POLL_MS = 15000;

export function useHostSession() {
  const { get } = useApi();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  // Listing controls — search + sort. State lives at the page level so it
  // persists while a user toggles between active session and the list.
  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState('latest'); // 'latest' | 'oldest' | 'name'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await get('/live/sessions');
      setSessions(list || []);
    } catch {
      /* keep stale */
    } finally {
      setLoading(false);
    }
  }, [get]);

  // Replace one session row in-place after a rename so the UI updates
  // instantly without re-fetching the whole list.
  const handleSessionRenamed = useCallback((updated) => {
    if (!updated?.session_id) return;
    setSessions((prev) => prev.map((s) =>
      s.session_id === updated.session_id ? { ...s, ...updated } : s,
    ));
    setActive((cur) => (cur?.session_id === updated.session_id ? { ...cur, ...updated } : cur));
  }, []);

  const selectSession = useCallback(async (sess) => {
    const fresh = await get(`/live/sessions/${sess.session_id}`);
    setActive(fresh);
  }, [get]);

  // Derived list — filter then sort. Memo would be nice but the list is
  // tiny (≤ 50 rows from the backend) so inlining keeps the file readable.
  const visibleSessions = (() => {
    const q = query.trim().toLowerCase();
    let rows = sessions;
    if (q) {
      rows = rows.filter((s) =>
        (s.quiz_title || '').toLowerCase().includes(q)
        || (s.ref_code || '').toLowerCase().includes(q)
      );
    }
    const tsOf = (s) => new Date(s.started_at || s.created_at || 0).getTime();
    if (sortMode === 'oldest') rows = [...rows].sort((a, b) => tsOf(a) - tsOf(b));
    else if (sortMode === 'name') rows = [...rows].sort((a, b) => (a.quiz_title || '').localeCompare(b.quiz_title || ''));
    else rows = [...rows].sort((a, b) => tsOf(b) - tsOf(a)); // latest first
    return rows;
  })();

  useEffect(() => {
    load();
  }, [load]);

  // Phase 2: deep-link from "Run live" CTA: /editor/live?session=ID
  useEffect(() => {
    const sid = searchParams.get('session');
    if (!sid || active?.session_id === sid) return;
    get(`/live/sessions/${sid}`)
      .then((s) => setActive(s))
      .catch(() => {
        toast.error('Session not found');
        navigate('/editor/live', { replace: true });
      });
  }, [searchParams, active, get, navigate]);

  // Polling refresh of the active session
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(async () => {
      try {
        const fresh = await get(`/live/sessions/${active.session_id}`);
        setActive(fresh);
      } catch {
        /* network blip */
      }
    }, POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.session_id, get]);

  // WS push refresh
  useLiveSocket(active?.session_id, {
    onEvent: async (msg) => {
      if (!active || !msg || msg.event === 'hello') return;
      try {
        const fresh = await get(`/live/sessions/${active.session_id}`);
        setActive(fresh);
      } catch {
        /* keep stale */
      }
    },
    onConnect: () => setWsConnected(true),
    onDisconnect: () => setWsConnected(false),
  });

  return {
    sessions,
    loading,
    active,
    setActive,
    wsConnected,
    query,
    setQuery,
    sortMode,
    setSortMode,
    visibleSessions,
    load,
    handleSessionRenamed,
    selectSession,
  };
}
